import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import { AdvancedCurrentPeopleFilterRoleUserApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ConfigurationRecap from './ConfigurationRecap';
import CustomSkillSelector from './CustomSkillSelector';
import { AGENT_TEMPLATES, TONE_PRESETS } from './consts';
import DocumentSelector from './DocumentSelector';
import SkillSelector from './SkillSelector';
import StructuredInstructionField from './StructuredInstructionField';
import { TemplateMetadata } from './useAgentMetadata';

interface WizardConfigurationParams {
  toggle: () => void;
  template_id: string;
  templates?: TemplateMetadata[];
}
export const useWizardConfiguration = ({
  toggle,
  template_id,
  templates = [],
}: WizardConfigurationParams) => {
  const { formatMessage } = useIntl();

  const template = useMemo(
    () => templates.find((t) => t.id === template_id),
    [templates, template_id]
  );

  const { skills, resources } = useMemo(
    () => ({
      skills: template?.skills || [],
      resources: template?.resources || [],
    }),
    [template]
  );

  const steps = useMemo(
    () => [
      // Choose Template
      {
        title: 'Choose template',
        helperText:
          'Start with an agent preset tailored to a common HR or manager use case.',
        inputs: [
          {
            required: true,
            name: 'initial_template',
            type: INPUT_TYPES.TILE_SELECTOR,
            options: AGENT_TEMPLATES(formatMessage),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject.initial_template === newValue) return {};
              const selectedTemplate = templates.find((t) => t.id === newValue);
              return {
                associated_skills: selectedTemplate?.skills || [],
                config_purpose: '',
                config_responsibilities: '',
                config_guidelines: '',
                config_custom_instructions: '',
              };
            },
          },
        ],
      },
      // Customize Basic Info
      {
        title: 'Customize basic info',
        helperText:
          'Set a name and description to help others understand the agent\u2019s purpose.',
        inputs: [
          {
            required: true,
            name: 'name',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.name.label',
              defaultMessage: 'Agent Name',
            }),
            placeholder: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.name.placeholder',
              defaultMessage: 'Enter agent name',
            }),
            type: INPUT_TYPES.TEXT,
            ignorePasswordManagers: true,
            autoFocus: true,
          },
          {
            required: false,
            name: 'description',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.description.label',
              defaultMessage: 'Description',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.description.help_text',
              defaultMessage: 'Provide a brief description of the agent.',
            }),
            placeholder: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.description.placeholder',
              defaultMessage: 'Enter a description (optional)',
            }),
            type: INPUT_TYPES.TEXTAREA,
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'config_purpose',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.purpose.label',
              defaultMessage: 'Agent role & purpose',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.purpose.help_text',
              defaultMessage:
                'Describe who this agent is and what primary outcome it is designed to support.',
            }),
            component: (props) => (
              <StructuredInstructionField
                {...props}
                baseContent={template?.purpose || ''}
              />
            ),
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'config_responsibilities',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.responsibilities.label',
              defaultMessage: 'Core responsibilities',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.responsibilities.help_text',
              defaultMessage:
                'List the types of problems or tasks this agent should handle.',
            }),
            component: (props) => (
              <StructuredInstructionField
                {...props}
                baseContent={template?.responsibilities || ''}
              />
            ),
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'config_guidelines',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.guidelines.label',
              defaultMessage: 'How the agent should use company data',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.guidelines.help_text',
              defaultMessage:
                'Specify how the agent should incorporate feedback, goals, ONA data, engagement data, etc.',
            }),
            component: (props) => (
              <StructuredInstructionField
                {...props}
                baseContent={template?.guidelines || ''}
              />
            ),
          },
          {
            required: false,
            minRows: 3,
            name: 'config_custom_instructions',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.custom_instructions.label',
              defaultMessage: 'Custom instructions',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.custom_instructions.help_text',
              defaultMessage:
                'Provide style, frameworks, guidelines, constraints, structure of responses, and/or when to recommend escalating to human.',
            }),
            placeholder: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.custom_instructions.placeholder',
              defaultMessage: 'Enter custom instructions (optional)',
            }),
            type: INPUT_TYPES.TEXTAREA,
          },
        ],
      },
      // Define Audience and Scoping
      {
        title: 'Define audience and scoping',
        helperText:
          'Use advanced filters to target departments, levels, locations, and more.',
        inputs: [
          {
            required: true,
            allowSelf: true,
            name: 'owners',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.owners.label',
              defaultMessage: 'Owners',
            }),
            type: INPUT_TYPES.PEOPLE_EDITOR,
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.create_agents_modal.create_modal.fields.owners.help_text',
              defaultMessage:
                'Select people who can manage this agent. If left empty, you will be set as the owner.',
            }),
          },
          {
            required: true,
            name: 'visibility',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.visibility.label',
              defaultMessage: 'Visibility',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.visibility.help_text',
              defaultMessage:
                'Set the visibility that will determine who can access the agent. The people with this access will be able to view it but not edit it. If set to custom, you must define a viewer filter.',
            }),
            type: INPUT_TYPES.DROPDOWN,
            objects: [
              {
                id: 'I',
                name: formatMessage({
                  id: 'app.views.ai.agents.agents_admin.visibility.private',
                  defaultMessage: 'Private',
                }),
              },
              {
                id: 'P',
                name: formatMessage({
                  id: 'app.views.ai.agents.agents_admin.visibility.public',
                  defaultMessage: 'Public',
                }),
              },
              {
                id: 'C',
                name: formatMessage({
                  id: 'app.views.ai.agents.agents_admin.visibility.custom',
                  defaultMessage: 'Custom',
                }),
              },
            ],
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            component: AdvancedCurrentPeopleFilterRoleUserApiComponent,
            name: 'viewers',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.viewers.label',
              defaultMessage: 'Viewers',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.viewers.help_text',
              defaultMessage:
                'Use advanced filters to specify which people can access this agent.',
            }),
          },
        ],
        renderInputs: ([owners, visibility, viewers]) => {
          const visibilityValue =
            visibility.props.value ?? visibility.props.defaultValue;
          return (
            <>
              {owners}
              {visibility}
              {visibilityValue === 'C' && viewers}
            </>
          );
        },
      },
      // Connect Content
      {
        title: 'Connect content',
        helperText:
          'Reference trusted sources from the repository for employee answers and internal reasoning.',
        inputs: [
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'associated_documents',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.documents.label',
              defaultMessage: 'Documents',
            }),
            component: DocumentSelector,
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'associated_skills',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.skills.label',
              defaultMessage: 'Built-in Skills',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.skills.help_text',
              defaultMessage:
                'Pre-configured capabilities that come with the selected template. These skills use platform data to guide the agent through common tasks.',
            }),
            component: (props) => (
              <SkillSelector {...props} availableSkills={skills} />
            ),
          },
          {
            required: false,
            type: INPUT_TYPES.CUSTOM_COMPONENT,
            name: 'custom_skills',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.custom_skills.label',
              defaultMessage: 'Custom Skills',
            }),
            helperText: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.custom_skills.help_text',
              defaultMessage:
                'Select custom skills created by your organization to give this agent specialized instructions.',
            }),
            component: CustomSkillSelector,
          },
        ],
        renderInputs: (
          [documents, skillsInput, customSkillsInput],
          _currentObjectRef
        ) => {
          const sectionStyle = {
            border: '1px solid #e3ebf6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            backgroundColor: '#f9fbfd',
          };
          const sectionTitleStyle: React.CSSProperties = {
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            color: '#6e84a3',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          };
          return (
            <>
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>
                  <i className="fe fe-file-text" />
                  {formatMessage({
                    id: 'app.views.ai.agents.agents_admin.connect_content.section.documents',
                    defaultMessage: 'Documents',
                  })}
                </div>
                {documents}
              </div>
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>
                  <i className="fe fe-zap" />
                  {formatMessage({
                    id: 'app.views.ai.agents.agents_admin.connect_content.section.built_in_skills',
                    defaultMessage: 'Built-in Skills',
                  })}
                </div>
                {React.cloneElement(skillsInput, {
                  skillsMetadata: skills,
                  resourcesMetadata: resources,
                })}
              </div>
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>
                  <i className="fe fe-tool" />
                  {formatMessage({
                    id: 'app.views.ai.agents.agents_admin.connect_content.section.custom_skills',
                    defaultMessage: 'Custom Skills',
                  })}
                </div>
                {React.cloneElement(customSkillsInput, {
                  resourcesMetadata: resources,
                })}
              </div>
            </>
          );
        },
      },
      // Set behavior & tone
      {
        title: 'Set behavior & tone',
        helperText:
          'Customize how the agent interacts with users to align with your company culture.',
        inputs: [
          {
            required: false,
            type: INPUT_TYPES.DROPDOWN,
            name: 'tone_preset',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.tone_preset.label',
              defaultMessage: 'Tone Preset',
            }),
            objects: TONE_PRESETS(formatMessage).map(({ id, name }) => ({
              id,
              name,
            })),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.tone_preset === newValue) return {};
              const selectedPreset = TONE_PRESETS(formatMessage).find(
                (preset) => preset.id === newValue
              );
              if (selectedPreset) {
                return {
                  challenge_level: selectedPreset.challenge_level,
                  empathy_balance: selectedPreset.empathy_balance,
                  directness: selectedPreset.directness,
                  formality: selectedPreset.formality,
                  proactivity: selectedPreset.proactivity,
                };
              }
            },
          },
          {
            required: false,
            name: 'challenge_level',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.challenge_level.label',
              defaultMessage: 'Challenge Level',
            }),
            type: INPUT_TYPES.SLIDER,
            showLabels: true,
            minLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.challenge_level.options.min_label',
              defaultMessage: 'Supportive',
            }),
            maxLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.challenge_level.options.max_label',
              defaultMessage: 'Push thinking',
            }),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.challenge_level === newValue) return {};
              return {
                tone_preset: 'CUSTOM_MIX',
              };
            },
          },
          {
            required: false,
            name: 'empathy_balance',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.empathy_balance.label',
              defaultMessage: 'Empathy Balance',
            }),
            type: INPUT_TYPES.SLIDER,
            showLabels: true,
            minLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.empathy_balance.options.min_label',
              defaultMessage: 'Matter-of-fact',
            }),
            maxLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.empathy_balance.options.max_label',
              defaultMessage: 'Warm & empathetic',
            }),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.empathy_balance === newValue) return {};
              return {
                tone_preset: 'CUSTOM_MIX',
              };
            },
          },
          {
            required: false,
            name: 'directness',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.directness.label',
              defaultMessage: 'Directness',
            }),
            type: INPUT_TYPES.SLIDER,
            showLabels: true,
            minLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.directness.options.min_label',
              defaultMessage: 'Exploratory',
            }),
            maxLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.directness.options.max_label',
              defaultMessage: 'Direct & concise',
            }),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.directness === newValue) return {};
              return {
                tone_preset: 'CUSTOM_MIX',
              };
            },
          },
          {
            required: false,
            name: 'formality',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.formality.label',
              defaultMessage: 'Formality',
            }),
            type: INPUT_TYPES.SLIDER,
            showLabels: true,
            minLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.formality.options.min_label',
              defaultMessage: 'Conversational',
            }),
            maxLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.formality.options.max_label',
              defaultMessage: 'Policy-guarded',
            }),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.formality === newValue) return {};
              return {
                tone_preset: 'CUSTOM_MIX',
              };
            },
          },
          {
            required: false,
            name: 'proactivity',
            label: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.proactivity.label',
              defaultMessage: 'Proactivity',
            }),
            type: INPUT_TYPES.SLIDER,
            showLabels: true,
            minLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.proactivity.options.min_label',
              defaultMessage: 'Reactive',
            }),
            maxLabel: formatMessage({
              id: 'app.views.ai.agents.agents_admin.edit_modal.fields.proactivity.options.max_label',
              defaultMessage: 'Anticipatory',
            }),
            onChangeSideEffects: (currentObject, newValue) => {
              if (currentObject?.proactivity === newValue) return {};
              return {
                tone_preset: 'CUSTOM_MIX',
              };
            },
          },
        ],
        renderInputs: ([
          tonePreset,
          challangeLevel,
          empathyBalance,
          directness,
          formality,
          proactivity,
        ]) => {
          return (
            <>
              {tonePreset}
              <Row>
                <Col className="col-6">{challangeLevel}</Col>
                <Col className="col-6">{empathyBalance}</Col>
              </Row>
              <Row>
                <Col className="col-6">{directness}</Col>
                <Col className="col-6">{formality}</Col>
              </Row>
              <Row>
                <Col className="col-6">{proactivity}</Col>
              </Row>
            </>
          );
        },
      },
      {
        title: 'Review & Publish',
        helperText:
          'Review your agent settings before publishing to ensure everything is correct.',
        inputs: [],
        renderInputs: ([], currentObjectRef) => {
          return (
            <ConfigurationRecap
              object={currentObjectRef?.current}
              onActionComplete={toggle}
            />
          );
        },
      },
    ],
    [formatMessage, toggle, skills, resources, templates, template]
  );
  const value = useMemo(() => ({ steps }), [steps]);

  return value;
};
